import React, { useEffect, useRef } from 'react';

export const OFFICES = [
  { city: 'Harare', country: 'Zimbabwe', lat: -17.83, lon: 31.05, tz: 'Africa/Harare' },
  { city: 'Johannesburg', country: 'South Africa', lat: -26.2, lon: 28.05, tz: 'Africa/Johannesburg' },
  { city: 'Prague', country: 'Czechia', lat: 50.08, lon: 14.44, tz: 'Europe/Prague' },
  { city: 'Frankfurt', country: 'Germany', lat: 50.11, lon: 8.68, tz: 'Europe/Berlin', side: 'l' },
  { city: 'New York', country: 'USA', lat: 40.71, lon: -74.0, tz: 'America/New_York' },
  { city: 'Sydney', country: 'Australia', lat: -33.87, lon: 151.21, tz: 'Australia/Sydney', side: 'l' },
];

// Rough footprint polygons [lat, lon]: brighter dots where we operate.
const AFRICA = [[37, -10], [37, 10], [31, 32], [12, 44], [11, 51], [-1, 42], [-12, 41], [-26, 35], [-35, 20], [-34, 18], [-18, 12], [-5, 10], [5, -8], [15, -17], [21, -17], [30, -10]];
const EUROPE = [[36, -9], [43, -9], [48, -5], [51, 2], [58, 5], [71, 25], [70, 30], [60, 30], [46, 38], [40, 26], [36, 23], [38, 15], [37, -5]];

const inside = (lat, lon, poly) => {
  let c = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [yi, xi] = poly[i], [yj, xj] = poly[j];
    if ((yi > lat) !== (yj > lat) && lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) c = !c;
  }
  return c;
};

const rad = (d) => (d * Math.PI) / 180;
const vec = (lat, lon) => [Math.cos(rad(lat)) * Math.sin(rad(lon)), Math.sin(rad(lat)), Math.cos(rad(lat)) * Math.cos(rad(lon))];

const DOTS = (() => {
  const n = 3200, out = [], g = Math.PI * (3 - Math.sqrt(5));
  for (let i = 0; i < n; i++) {
    const y = 1 - (i / (n - 1)) * 2, r = Math.sqrt(1 - y * y), t = g * i;
    const lat = (Math.asin(y) * 180) / Math.PI, lon = ((Math.atan2(Math.sin(t) * r, Math.cos(t) * r) * 180) / Math.PI);
    out.push({ v: [r * Math.sin(t), y, r * Math.cos(t)], land: inside(lat, lon, AFRICA) || inside(lat, lon, EUROPE) });
  }
  return out;
})();

export const ARCS = [[0, 2], [1, 2], [0, 1], [3, 4], [3, 0], [2, 3], [5, 1]];

// `view` is a ref: { rot, tilt } targets in radians. `auto` spins it slowly when no target is driven.
const Globe = ({ view, auto = false, className = '' }) => {
  const cv = useRef(null);

  useEffect(() => {
    const canvas = cv.current, ctx = canvas.getContext('2d');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let w = 0, h = 0, raf, visible = true, rot = view.current.rot, tilt = view.current.tilt, t0 = performance.now();

    const fit = () => {
      const r = canvas.getBoundingClientRect(), d = Math.min(window.devicePixelRatio || 1, 2);
      w = r.width; h = r.height; canvas.width = w * d; canvas.height = h * d; ctx.setTransform(d, 0, 0, d, 0, 0);
    };
    const ro = new ResizeObserver(fit); ro.observe(canvas); fit();
    const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; }, { threshold: 0 }); io.observe(canvas);

    const frame = (now) => {
      raf = requestAnimationFrame(frame);
      if (!visible) return;
      const cs = getComputedStyle(document.documentElement);
      const paper = cs.getPropertyValue('--paper').trim(), red = cs.getPropertyValue('--red').trim();
      if (auto && !reduce) view.current.rot += 0.0016;
      rot += (view.current.rot - rot) * 0.06; tilt += (view.current.tilt - tilt) * 0.06;

      const R = Math.min(w, h) * 0.4, cx = w / 2, cy = h / 2, cr = Math.cos(rot), sr = Math.sin(rot), ct = Math.cos(tilt), st = Math.sin(tilt);
      const project = (v, lift = 1) => {
        const x0 = v[0] * cr - v[2] * sr, z0 = v[0] * sr + v[2] * cr;
        const y1 = v[1] * ct - z0 * st, z1 = v[1] * st + z0 * ct;
        return [cx + x0 * R * lift, cy - y1 * R * lift, z1];
      };

      ctx.clearRect(0, 0, w, h);
      // atmosphere
      const gr = ctx.createRadialGradient(cx, cy, R * 0.9, cx, cy, R * 1.25);
      gr.addColorStop(0, red + '00'); gr.addColorStop(0.3, red + '2a'); gr.addColorStop(1, red + '00');
      ctx.fillStyle = gr; ctx.beginPath(); ctx.arc(cx, cy, R * 1.25, 0, 7); ctx.fill();
      ctx.strokeStyle = paper; ctx.globalAlpha = 0.12; ctx.lineWidth = 1; ctx.beginPath(); ctx.arc(cx, cy, R, 0, 7); ctx.stroke();

      for (const d of DOTS) {
        const [x, y, z] = project(d.v);
        if (z < 0) continue;
        ctx.globalAlpha = d.land ? 0.35 + 0.65 * z : 0.12 + 0.22 * z;
        ctx.fillStyle = d.land ? paper : paper;
        const s = d.land ? 2 : 1.3;
        ctx.fillRect(x - s / 2, y - s / 2, s, s);
      }

      // arcs with a travelling pulse
      const tt = ((now - t0) / 4200) % 1;
      ctx.lineWidth = 1.4; ctx.strokeStyle = red; ctx.fillStyle = red;
      ARCS.forEach(([a, b], k) => {
        const A = vec(OFFICES[a].lat, OFFICES[a].lon), B = vec(OFFICES[b].lat, OFFICES[b].lon), steps = 48;
        let prev = null;
        for (let i = 0; i <= steps; i++) {
          const f = i / steps, m = [A[0] + (B[0] - A[0]) * f, A[1] + (B[1] - A[1]) * f, A[2] + (B[2] - A[2]) * f];
          const len = Math.hypot(...m) || 1, lift = (1 + 0.28 * Math.sin(Math.PI * f)) / len;
          const p = project(m.map((c) => c * lift));
          if (prev && p[2] > -0.05 && prev[2] > -0.05) { ctx.globalAlpha = 0.55; ctx.beginPath(); ctx.moveTo(prev[0], prev[1]); ctx.lineTo(p[0], p[1]); ctx.stroke(); }
          prev = p;
        }
        const f = (tt + k * 0.33) % 1, m = [A[0] + (B[0] - A[0]) * f, A[1] + (B[1] - A[1]) * f, A[2] + (B[2] - A[2]) * f];
        const len = Math.hypot(...m) || 1, p = project(m.map((c) => (c * (1 + 0.28 * Math.sin(Math.PI * f))) / len));
        if (p[2] > 0) { ctx.globalAlpha = 1; ctx.beginPath(); ctx.arc(p[0], p[1], 2.6, 0, 7); ctx.fill(); }
      });

      // office markers
      OFFICES.forEach((o) => {
        const [x, y, z] = project(vec(o.lat, o.lon));
        if (z < 0.02) return;
        const pulse = ((now / 1600) % 1);
        ctx.strokeStyle = red; ctx.globalAlpha = (1 - pulse) * 0.8; ctx.beginPath(); ctx.arc(x, y, 4 + pulse * 18, 0, 7); ctx.stroke();
        ctx.globalAlpha = 1; ctx.fillStyle = red; ctx.beginPath(); ctx.arc(x, y, 4.5, 0, 7); ctx.fill();
        ctx.fillStyle = paper; ctx.font = '500 11px "JetBrains Mono", monospace'; ctx.fillText(o.city.toUpperCase(), x + 12, y + 4);
      });
      ctx.globalAlpha = 1;
    };
    raf = requestAnimationFrame(frame);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); io.disconnect(); };
  }, [view, auto]);

  return <canvas ref={cv} className={className} role="img" aria-label="Globe showing enseQure offices in Harare, Johannesburg and Prague" />;
};

export default Globe;
